import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { TimeOffRequest, TimeOffStatus } from "@/api/types";

export type TimeOffLoadStatus = "loading" | "ready" | "error";

export interface UsePendingTimeOffResult {
  /** `null` until resolved and on failure — never `[]`, which is the real and different
   *  answer "the queue is empty, there is nothing to review" (same convention
   *  `usePendingTime` uses for the time-approval queue). */
  entries: TimeOffRequest[] | null;
  status: TimeOffLoadStatus;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * The owner's leave-request review queue — GET /api/demo/time-off/pending.
 *
 * Owner-only server-side (403 for an employee login). This hook does not check that and
 * must not — same reasoning `usePendingTime` gives for the time-approval queue this
 * mirrors. Re-read after every decision rather than patched locally: a 409 means somebody
 * else already decided it (or the same tap landed twice), and the honest response to a
 * stale queue is to fetch it again.
 */
export function usePendingTimeOff(): UsePendingTimeOffResult {
  const [entries, setEntries] = useState<TimeOffRequest[] | null>(null);
  const [status, setStatus] = useState<TimeOffLoadStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const resolved = await demoApi.getPendingTimeOff(signal);
      if (signal?.aborted) return;
      setEntries(resolved);
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setEntries(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the time-off queue."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  return { entries, status, error, reload };
}

export interface UseTimeOffHistoryResult {
  /** `null` until resolved and on failure. `[]` is the real, different answer — nothing
   *  decided (or filed) in this window under this filter. */
  entries: TimeOffRequest[] | null;
  status: TimeOffLoadStatus;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * The owner's decided-request history — GET /api/demo/time-off?status=. `"all"` omits
 * the filter entirely rather than sending four separate requests unioned client-side;
 * the server's own default 90-day window (filed date, not leave date — see
 * `ScheduleRepository.GetTimeOffHistoryAsync`'s remarks) applies either way.
 *
 * `null` means NO FILTER HAS BEEN CHOSEN YET, and is deliberately different from `"all"`:
 * it skips the fetch entirely rather than asking the server for everything. Two reasons,
 * one of them load-bearing:
 *
 *   - The SAME `id` this route can return for a still-`pending` request is also the `id`
 *     `usePendingTimeOff` renders in the queue above History on this same screen — a
 *     `[data-time-off-id]` cannot honestly appear twice on one page. Fetching eagerly with
 *     any status that could include `pending` (or omitting status, which does) risked
 *     exactly that collision the moment a request was genuinely still pending, which
 *     `time-off.spec.ts`'s own strict-mode locators caught directly: TWO elements resolved
 *     for one id.
 *   - `time-off.spec.ts`'s AC4 decides a request OUT OF BAND (a second tab, or a race) and
 *     then asserts its marker appears NOWHERE on the page — which only holds if the very
 *     first render does not already query every decided record in the window.
 *
 * A fresh fetch on every `status` change rather than one broad fetch filtered in the
 * browser: the server is the one place "what counts as denied" is decided, and asking it
 * again keeps this screen from silently drifting out of step with that rule.
 */
export function useTimeOffHistory(status: TimeOffStatus | "all" | null): UseTimeOffHistoryResult {
  const [entries, setEntries] = useState<TimeOffRequest[] | null>(null);
  const [loadStatus, setLoadStatus] = useState<TimeOffLoadStatus>(status === null ? "ready" : "loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (status === null) {
        setEntries([]);
        setLoadStatus("ready");
        setError(null);
        return;
      }
      try {
        const resolved = await demoApi.getTimeOffHistory(
          status === "all" ? undefined : status,
          undefined,
          signal,
        );
        if (signal?.aborted) return;
        setEntries(resolved);
        setLoadStatus("ready");
        setError(null);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setEntries(null);
        setLoadStatus("error");
        setError(problemMessage(err, "Could not load time-off history."));
      }
    },
    [status],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  return { entries, status: loadStatus, error, reload };
}
